import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FileSliders, Save, ArrowLeft, Download, RefreshCw,
  Layers, BarChart3, Target, AlertTriangle, CheckCircle,
  FileText, Table, TrendingUp, Zap
} from 'lucide-react';
import api, { downloadBlob } from '../services/api';
import './PresentationCreate.css';

const SLIDE_TYPE_ICONS = {
  title:              { icon: FileSliders, color: '#1e3a8a' },
  key_metrics:        { icon: Target,      color: '#059669' },
  dynamics:           { icon: TrendingUp,  color: '#3b82f6' },
  department_ranking: { icon: BarChart3,   color: '#8b5cf6' },
  discipline:         { icon: CheckCircle, color: '#10b981' },
  plan_fact:          { icon: Table,       color: '#f59e0b' },
  problems_risks:     { icon: AlertTriangle, color: '#ef4444' },
  conclusions:        { icon: Zap,         color: '#6366f1' },
  custom_text:        { icon: FileText,    color: '#64748b' },
};

function PresentationCreate() {
  const { t } = useTranslation(['presentations', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const isView = Boolean(id);

  const [templates, setTemplates] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    presentation_type: 'period_results',
    year: new Date().getFullYear(),
    template: '',
    department: '',
  });

  const [presentation, setPresentation] = useState(null);
  const [slides, setSlides] = useState([]);

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 2, currentYear - 1, currentYear, currentYear + 1];

  useEffect(() => {
    fetchReferenceData();
    if (isView) {
      fetchPresentation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchReferenceData = async () => {
    try {
      const [tplRes, deptRes] = await Promise.all([
        api.getPresentationTemplates({ is_active: true }),
        api.getDepartments(),
      ]);
      const tplData = tplRes.data.results || tplRes.data;
      setTemplates(tplData);

      // Если нет шаблонов — создать системные
      if (tplData.length === 0) {
        await api.createDefaultTemplates();
        const freshRes = await api.getPresentationTemplates({ is_active: true });
        setTemplates(freshRes.data.results || freshRes.data);
      }

      setDepartments(deptRes.data.results || deptRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching reference data:', error);
      setLoading(false);
    }
  };

  const fetchPresentation = async () => {
    try {
      const [presRes, previewRes] = await Promise.all([
        api.getPresentation(id),
        api.previewPresentation(id).catch(() => ({ data: { slides: [] } })),
      ]);
      const pres = presRes.data;
      setPresentation(pres);
      setFormData({
        title: pres.title,
        presentation_type: pres.presentation_type,
        year: pres.year,
        template: pres.template || '',
        department: pres.department || '',
      });
      setSlides(previewRes.data.slides || pres.slides || []);
    } catch (error) {
      console.error('Error fetching presentation:', error);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert(t('presentations:form.titleRequired'));
      return;
    }
    if (!formData.template) {
      alert(t('presentations:form.templateRequired'));
      return;
    }

    setSaving(true);
    try {
      const data = {
        title: formData.title,
        presentation_type: formData.presentation_type,
        year: formData.year,
        template: formData.template,
        department: formData.department || null,
      };

      if (isView && presentation) {
        await api.updatePresentation(presentation.id, data);
        navigate(`/presentations/${presentation.id}`);
      } else {
        const resp = await api.createPresentation(data);
        navigate(`/presentations/${resp.data.id}`);
      }
    } catch (error) {
      console.error('Error saving presentation:', error);
      alert(t('presentations:messages.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!presentation) return;
    setGenerating(true);
    try {
      const resp = await api.generatePresentation(presentation.id);
      setPresentation(resp.data);
      setSlides(resp.data.slides || []);
    } catch (error) {
      console.error('Error generating:', error);
      alert(t('presentations:messages.generateError'));
    } finally {
      setGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    if (!presentation) return;
    setGenerating(true);
    try {
      const resp = await api.regeneratePresentation(presentation.id);
      setPresentation(resp.data);
      setSlides(resp.data.slides || []);
    } catch (error) {
      console.error('Error regenerating:', error);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!presentation) return;
    try {
      const r = await api.downloadPresentation(presentation.id);
      downloadBlob(r, `${presentation.title}.pptx`);
    } catch (error) {
      alert(t('presentations:messages.downloadError'));
    }
  };

  const renderSlidePreview = (slide) => {
    const iconInfo = SLIDE_TYPE_ICONS[slide.slide_type] || SLIDE_TYPE_ICONS.custom_text;
    const Icon = iconInfo.icon;
    const slideLabel = t(`presentations:slideTypes.${slide.slide_type}`, { defaultValue: slide.slide_type });
    const data = slide.content_data || {};

    return (
      <div key={slide.id} className="slide-preview-card">
        <div className="slide-header">
          <div className="slide-number" style={{ background: iconInfo.color }}>
            {slide.order}
          </div>
          <div className="slide-meta">
            <span className="slide-type-label" style={{ color: iconInfo.color }}>
              <Icon size={16} />
              {slideLabel}
            </span>
            <span className="slide-title">{slide.title || slideLabel}</span>
          </div>
        </div>
        <div className="slide-content-preview">
          {slide.slide_type === 'title' && (
            <div className="preview-title-slide">
              <strong>{data.title || t('presentations:slidePreview.noTitle')}</strong>
              <span>{data.department || ''} | {data.subtitle || ''}</span>
            </div>
          )}
          {slide.slide_type === 'key_metrics' && data.metrics && (
            <div className="preview-metrics">
              {data.metrics.slice(0, 4).map((m, i) => (
                <div key={i} className="preview-metric-card">
                  <div className="metric-value">{m.value}</div>
                  <div className="metric-label">{m.label}</div>
                </div>
              ))}
            </div>
          )}
          {slide.slide_type === 'dynamics' && (
            <div className="preview-chart-placeholder">
              <BarChart3 size={32} />
              <span>{t('presentations:slidePreview.chartDataPoints', { count: (data.categories || []).length })}</span>
            </div>
          )}
          {slide.slide_type === 'department_ranking' && (
            <div className="preview-chart-placeholder">
              <BarChart3 size={32} />
              <span>{t('presentations:slidePreview.rankingDepts', { count: (data.categories || []).length })}</span>
            </div>
          )}
          {slide.slide_type === 'discipline' && (
            <div className="preview-discipline">
              <span>{t('presentations:slidePreview.documentsRate', { rate: data.documents?.rate || 0 })}</span>
              <span>{t('presentations:slidePreview.tasksRate', { rate: data.tasks?.rate || 0 })}</span>
            </div>
          )}
          {slide.slide_type === 'plan_fact' && (
            <div className="preview-table-info">
              <Table size={20} />
              <span>{t('presentations:slidePreview.tableRows', { count: (data.rows || []).length })}</span>
            </div>
          )}
          {slide.slide_type === 'problems_risks' && (
            <div className="preview-problems">
              <span>{t('presentations:slidePreview.problemsCount', { count: (data.problems || []).length })}</span>
              <span>{t('presentations:slidePreview.risksCount', { count: (data.risks || []).length })}</span>
            </div>
          )}
          {slide.slide_type === 'conclusions' && (
            <div className="preview-conclusions">
              <span>{t('presentations:slidePreview.achievementsCount', { count: (data.achievements || []).length })}</span>
              <span>{t('presentations:slidePreview.recommendationsCount', { count: (data.recommendations || []).length })}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pres-create-page">
        <div className="loading-spinner">{t('common:table.loading')}</div>
      </div>
    );
  }

  return (
    <div className="pres-create-page">
      {/* Header */}
      <div className="pres-create-header">
        <h1>
          <FileSliders size={24} />
          {isView ? (presentation?.title || t('presentations:presentation')) : t('presentations:createPresentation')}
        </h1>
        <div className="header-actions">
          {isView && presentation && slides.length > 0 && (
            <>
              <button className="btn-secondary" onClick={handleRegenerate} disabled={generating}>
                <RefreshCw size={16} className={generating ? 'spinning' : ''} />
                {t('presentations:regenerate')}
              </button>
              <button className="btn-download-pptx" onClick={handleDownload}>
                <Download size={16} />
                {t('presentations:download')}
              </button>
            </>
          )}
          {isView && presentation && slides.length === 0 && (
            <button className="btn-generate" onClick={handleGenerate} disabled={generating}>
              <Zap size={16} />
              {generating ? t('presentations:generating') : t('presentations:generate')}
            </button>
          )}
          {!isView && (
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              <Save size={16} />
              {saving ? t('admin:settings.saving') : t('common:buttons.save')}
            </button>
          )}
          <button className="btn-secondary" onClick={() => navigate('/presentations')}>
            <ArrowLeft size={16} />
            {t('common:buttons.back')}
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="form-card">
        <h2>
          <Layers size={20} />
          {t('presentations:form.params')}
        </h2>

        <div className="form-row">
          <div className="form-group" style={{ flex: 2 }}>
            <label>{t('presentations:form.titleLabel')}</label>
            <input
              type="text"
              className="form-control"
              placeholder={t('presentations:form.titlePlaceholder')}
              value={formData.title}
              onChange={e => setFormData({ ...formData, title: e.target.value })}
              readOnly={isView}
            />
          </div>
          <div className="form-group">
            <label>{t('presentations:form.typeLabel')}</label>
            <select
              className="form-control"
              value={formData.presentation_type}
              onChange={e => setFormData({ ...formData, presentation_type: e.target.value })}
              disabled={isView}
            >
              <option value="period_results">{t('presentations:types.period_results')}</option>
              <option value="achievements">{t('presentations:types.achievements')}</option>
              <option value="analytics">{t('presentations:types.analytics')}</option>
              <option value="meeting">{t('presentations:types.meeting')}</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>{t('presentations:form.templateLabel')}</label>
            <select
              className="form-control"
              value={formData.template}
              onChange={e => setFormData({ ...formData, template: e.target.value })}
              disabled={isView}
            >
              <option value="">{t('presentations:form.selectTemplate')}</option>
              {templates.map(tpl => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name} ({tpl.template_type_display})
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('presentations:form.yearLabel')}</label>
            <select
              className="form-control"
              value={formData.year}
              onChange={e => setFormData({ ...formData, year: parseInt(e.target.value) })}
              disabled={isView}
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('presentations:form.departmentLabel')}</label>
            <select
              className="form-control"
              value={formData.department}
              onChange={e => setFormData({ ...formData, department: e.target.value })}
              disabled={isView}
            >
              <option value="">{t('presentations:form.allOrg')}</option>
              {departments.map(d => (
                <option key={d.id} value={d.id}>{d.name_ru}</option>
              ))}
            </select>
          </div>
        </div>

        {formData.template && !isView && (
          <div className="template-info">
            {(() => {
              const tpl = templates.find(t => t.id === parseInt(formData.template));
              if (!tpl) return null;
              return (
                <div className="template-details">
                  <strong>{tpl.name}</strong>
                  <span>{tpl.description}</span>
                  <div className="template-slides-preview">
                    {(tpl.slide_structure || []).filter(s => s.enabled).map((s, i) => {
                      const iconInfo = SLIDE_TYPE_ICONS[s.type] || SLIDE_TYPE_ICONS.custom_text;
                      return (
                        <span key={i} className="template-slide-chip" style={{ borderColor: iconInfo.color, color: iconInfo.color }}>
                          {t(`presentations:slideTypes.${s.type}`, { defaultValue: s.type })}
                        </span>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>

      {/* Slides Preview */}
      {isView && slides.length > 0 && (
        <div className="slides-section">
          <h2>
            <Layers size={20} />
            {t('presentations:slides')} ({slides.length})
          </h2>
          <div className="slides-list">
            {slides.map(slide => renderSlidePreview(slide))}
          </div>
        </div>
      )}

      {isView && slides.length === 0 && presentation && (
        <div className="generate-prompt">
          <Zap size={48} />
          <h3>{t('presentations:empty.notGenerated')}</h3>
          <p>{t('presentations:empty.notGeneratedHint')}</p>
          <button className="btn-generate" onClick={handleGenerate} disabled={generating}>
            <Zap size={18} />
            {generating ? t('presentations:generating') : t('presentations:generatePresentation')}
          </button>
        </div>
      )}
    </div>
  );
}

export default PresentationCreate;
